import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { StoreProvider } from "@/context/StoreContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import Home from "@/pages/Home"; // eager (LCP)
import Shop from "@/pages/Shop"; // eager (2nd most common landing)
import ProductDetail from "@/pages/ProductDetail"; // eager (deep-links)

// Non-critical routes — code-split
const Checkout = lazy(() => import("@/pages/Checkout"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const Collections = lazy(() => import("@/pages/Collections"));
const About = lazy(() => import("@/pages/About"));
const Journal = lazy(() => import("@/pages/Journal"));
const Contact = lazy(() => import("@/pages/Contact"));
const Account = lazy(() => import("@/pages/Account"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const Lookbook = lazy(() => import("@/pages/Lookbook"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Policy = lazy(() => import("@/pages/Policy"));
const ExitIntentPopup = lazy(() =>
  import("@/components/ExitIntentPopup").then((m) => ({ default: m.ExitIntentPopup }))
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
};

const PageFallback = () => <div className="min-h-screen bg-black" />;

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <ScrollToTop />
          <Navbar />
          <main className="min-h-screen bg-black">
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/collections" element={<Collections />} />
                <Route path="/about" element={<About />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/journal/:slug" element={<Journal />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/account" element={<Account />} />
                <Route path="/account/reset-password" element={<ResetPassword />} />
                <Route path="/lookbook" element={<Lookbook />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/shipping-policy" element={<Policy type="shipping" />} />
                <Route path="/returns" element={<Policy type="returns" />} />
                <Route path="/privacy" element={<Policy type="privacy" />} />
                <Route path="/terms" element={<Policy type="terms" />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <CartDrawer />
          <Suspense fallback={null}>
            <ExitIntentPopup />
          </Suspense>
          <Toaster position="bottom-right" theme="dark" />
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
